import Icon, { IconType } from '../components/icon';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import * as css from './menu.module.scss';
import translations from './menu.i18n';
import { devEnvFlag } from '../util/featureFlags';
import classNames from 'classnames';

type onClickType = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;

interface MenuItemProps {
  title: string;
  path: string;
  iconType: IconType;
  onClick?: onClickType;
  exact?: boolean;
}

export const MenuItem = ({
  title,
  path,
  iconType,
  onClick,
  exact,
}: MenuItemProps) => {
  return (
    <li>
      <NavLink
        onClick={onClick}
        to={{ pathname: path }}
        className={({ isActive }) =>
          classNames(css.menuItem, isActive && css.active)
        }
        end={exact}
      >
        <div className={css.innerItemBox}>
          <span className={css.icon}>
            <Icon type={iconType} title={''} className={css.secondaryIcon} />
          </span>
          <span className={css.name}>{title}</span>
        </div>
      </NavLink>
    </li>
  );
};

const logoutEventHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  window.location.href = '/login/logout';
};

const DashboardItem = () => {
  const { formatMessage } = useIntl();
  const dashboard = useMemo(
    () => formatMessage({ id: translations.dashboard.id }),
    [formatMessage],
  );

  return (
    <MenuItem title={dashboard} iconType={IconType.Dashboard} path="/" exact />
  );
};

const SettingsItem = () => {
  const { formatMessage } = useIntl();
  const settings = useMemo(
    () => formatMessage({ id: translations.settings.id }),
    [formatMessage],
  );

  return (
    <MenuItem title={settings} iconType={IconType.Settings} path="/settings" />
  );
};

const LogoutItem = () => {
  const { formatMessage } = useIntl();
  const logout = useMemo(
    () => formatMessage({ id: translations.logout.id }),
    [formatMessage],
  );

  return (
    <MenuItem
      onClick={logoutEventHandler}
      title={logout}
      iconType={IconType.ExitToAppRounded}
      path="/login/logout"
      exact
    />
  );
};

export const MenuItemsUHA = () => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      calendar: formatMessage({ id: translations.calendar.id }),
      myinstitution: formatMessage({ id: translations.myinstitution.id }),
      practiceplaces: formatMessage({ id: translations.practiceplaces.id }),
      vurdering: formatMessage({ id: translations.vurdering.id }),
    }),
    [formatMessage],
  );

  return (
    <ul>
      <DashboardItem />
      <MenuItem
        title={labels.calendar}
        iconType={IconType.DateRange}
        path="/calendar"
      />
      {devEnvFlag && (
        <MenuItem
          title={labels.vurdering}
          iconType={IconType.Evaluation}
          path="/evaluation"
        />
      )}
      <MenuItem
        title={labels.myinstitution}
        iconType={IconType.AccountBalance}
        path="/myinstitution"
      />
      <MenuItem
        title={labels.practiceplaces}
        iconType={IconType.EndNode}
        path="/practiceplaces"
      />
      <SettingsItem />
      <LogoutItem />
    </ul>
  );
};

export const MenuItemsPSA = () => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      calendar: formatMessage({ id: translations.calendar.id }),
      myorganization: formatMessage({ id: translations.myorganization.id }),
      students: formatMessage({ id: translations.students.id }),
      vurdering: formatMessage({ id: translations.vurdering.id }),
    }),
    [formatMessage],
  );

  return (
    <ul>
      <DashboardItem />
      <MenuItem
        title={labels.calendar}
        iconType={IconType.DateRange}
        path="/calendar"
      />
      {devEnvFlag && (
        <MenuItem
          title={labels.vurdering}
          iconType={IconType.Evaluation}
          path="/evaluation"
        />
      )}
      <MenuItem
        title={labels.myorganization}
        iconType={IconType.EndNode}
        path="/myorganization"
      />
      <MenuItem
        title={labels.students}
        iconType={IconType.Group}
        path="/students"
      />
      <SettingsItem />
      <LogoutItem />
    </ul>
  );
};

export const MenuItemsStudent = () => {
  return (
    <ul>
      <DashboardItem />
      <SettingsItem />
      <LogoutItem />
    </ul>
  );
};

export const MenuItemsAdmin = () => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      praksissteder: formatMessage({ id: translations.practicePlaceSearch.id }),
      synkronisering: formatMessage({ id: translations.sync.id }),
      mineBrukere: formatMessage({ id: translations.mineBrukere.id }),
      lagStudiestedbruker: formatMessage({
        id: translations.lagStudiestedbruker.id,
      }),
      inviterSystembruker: formatMessage({
        id: translations.inviterSystembruker.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <ul>
      <MenuItem
        title={labels.praksissteder}
        path="/"
        iconType={IconType.Search}
      />
      <MenuItem
        title={labels.synkronisering}
        path="/sync"
        iconType={IconType.DateRange}
      />
      <MenuItem
        title={labels.mineBrukere}
        path="/myusers"
        iconType={IconType.Group}
      />
      <MenuItem
        title={labels.lagStudiestedbruker}
        path="/addstudiestedbruker"
        iconType={IconType.AccountBalance}
      />
      <MenuItem
        title={labels.inviterSystembruker}
        path="/addsystembruker"
        iconType={IconType.AddCircleRounded}
      />
      <LogoutItem />
    </ul>
  );
};
